import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "../api";
import { PageHeader, Card, Btn, Input, Field, Badge, Spinner, Empty } from "../ui";
import { useAuth, usePermissions } from "../AuthContext";
import { AjutorButton } from "../AjutorButton";
import { UserCog, Plus, Pencil, Trash2, X, Shield, UserPlus, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const genPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
};

const emptyPerms = (modules, actions) =>
  modules.reduce((acc, m) => {
    acc[m.key] = actions.reduce((a, ac) => ({ ...a, [ac.key]: false }), {});
    return acc;
  }, {});

function RoleEditor({ draft, setDraft, modules, actions, canEdit, onSave, onClose, saving }) {
  const toggle = (mk, ak) => {
    setDraft((d) => ({
      ...d,
      permissions: {
        ...d.permissions,
        [mk]: { ...(d.permissions[mk] || {}), [ak]: !((d.permissions[mk] || {})[ak]) },
      },
    }));
  };
  const editable = canEdit && !(draft.system && draft._readonly);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl h-full bg-white overflow-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="util-role-editor">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">
            {draft.id ? "Editeaza rol" : "Rol nou"}
          </h3>
          <button onClick={onClose} data-testid="util-role-editor-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-5">
          <Field label="Denumire rol *">
            <Input data-testid="util-role-name-input" value={draft.name}
              disabled={!editable}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
          </Field>
          {draft.system && (
            <p className="text-xs text-amber-600 flex items-center gap-1">
              <Shield className="w-3.5 h-3.5" /> Rol de sistem.
            </p>
          )}
          <div className="overflow-x-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm" data-testid="util-perm-matrix">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-100 bg-slate-50">
                  <th className="px-4 py-2.5 font-medium">Ecran</th>
                  {actions.map((a) => (
                    <th key={a.key} className="px-3 py-2.5 font-medium text-center">{a.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {modules.map((m) => (
                  <tr key={m.key} className="border-b border-slate-50">
                    <td className="px-4 py-2.5 text-slate-800">{m.label}</td>
                    {actions.map((a) => (
                      <td key={a.key} className="px-3 py-2.5 text-center">
                        <input type="checkbox"
                          data-testid={`util-perm-${m.key}-${a.key}`}
                          disabled={!editable}
                          checked={!!((draft.permissions[m.key] || {})[a.key])}
                          onChange={() => toggle(m.key, a.key)} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editable && (
            <div className="flex justify-end gap-2">
              <Btn variant="outline" type="button" onClick={onClose}>Anuleaza</Btn>
              <Btn onClick={onSave} disabled={saving} data-testid="util-save-role-btn">
                {saving ? "Se salveaza..." : "Salveaza"}
              </Btn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function UserForm({ draft, setDraft, roles, onSave, onClose, saving }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-md h-full bg-white overflow-auto shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="util-user-form">
        <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-900">Utilizator nou</h3>
          <button onClick={onClose} data-testid="util-user-form-close"><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Nume *">
            <Input data-testid="util-user-name-input" value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} />
          </Field>
          <Field label="Email *">
            <Input data-testid="util-user-email-input" type="email" value={draft.email}
              onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))} />
          </Field>
          <Field label="Parola *">
            <div className="flex gap-2">
              <Input data-testid="util-user-password-input" value={draft.password}
                onChange={(e) => setDraft((d) => ({ ...d, password: e.target.value }))} />
              <Btn variant="outline" type="button" title="Genereaza parola"
                onClick={() => setDraft((d) => ({ ...d, password: genPassword() }))}
                data-testid="util-user-genpass-btn"><RefreshCw className="w-4 h-4" /></Btn>
            </div>
          </Field>
          <Field label="Rol *">
            <select data-testid="util-user-role-input" value={draft.role_key}
              onChange={(e) => setDraft((d) => ({ ...d, role_key: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm outline-none focus:border-emerald-500">
              {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
            </select>
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <Btn variant="outline" type="button" onClick={onClose}>Anuleaza</Btn>
            <Btn onClick={onSave} disabled={saving} data-testid="util-user-save-btn">
              {saving ? "Se salveaza..." : "Salveaza"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Utilizatori() {
  const { isSuperAdmin, tenantId, user, refreshPerms } = useAuth();
  const { can } = usePermissions();
  const scope = isSuperAdmin ? (tenantId ? "tenant" : "platform") : "tenant";

  const [modules, setModules] = useState([]);
  const [actions, setActions] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [userDraft, setUserDraft] = useState(null);
  const [savingUser, setSavingUser] = useState(false);

  const canAdd = can("utilizatori", "add");
  const canEdit = can("utilizatori", "edit");
  const canDelete = can("utilizatori", "delete");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mod, rl, us] = await Promise.all([
        api.get("/roles/modules"),
        api.get("/roles", { params: { scope } }),
        api.get("/users"),
      ]);
      const acts = mod.data.actions || [];
      const mods = scope === "platform" ? mod.data.platform_modules : mod.data.tenant_modules;
      setActions(acts);
      setModules(mods || []);
      setRoles(rl.data.items || []);
      setUsers(us.data.items || []);
    } catch (e) {
      toast.error(apiError(e, "Nu s-au putut incarca rolurile"));
    } finally {
      setLoading(false);
    }
  }, [scope]);
  useEffect(() => { load(); }, [load]);

  const newRole = () => {
    setDraft({ id: null, name: "", system: false, permissions: emptyPerms(modules, actions) });
  };
  const editRole = (r) => {
    const base = emptyPerms(modules, actions);
    modules.forEach((m) => {
      const p = r.permissions?.[m.key] || {};
      actions.forEach((a) => { base[m.key][a.key] = !!p[a.key]; });
    });
    const readonly = r.system && !isSuperAdmin;
    setDraft({ id: r.id, name: r.name, system: r.system, key: r.key, permissions: base, _readonly: readonly });
  };

  const saveRole = async () => {
    if (!draft.name || draft.name.trim().length < 2) { toast.error("Denumirea rolului este obligatorie"); return; }
    setSaving(true);
    try {
      if (draft.id) {
        await api.patch(`/roles/${draft.id}`, { name: draft.name, permissions: draft.permissions });
        toast.success("Rol actualizat");
      } else {
        await api.post("/roles", { name: draft.name, scope, permissions: draft.permissions });
        toast.success("Rol creat");
      }
      setDraft(null);
      await load();
      await refreshPerms();
    } catch (e) {
      toast.error(apiError(e, "Nu s-a putut salva rolul"));
    } finally { setSaving(false); }
  };

  const delRole = async (r) => {
    if (!window.confirm(`Stergi rolul "${r.name}"?`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      toast.success("Rol sters");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const assignRole = async (u, roleKey) => {
    try {
      await api.patch(`/users/${u.id}/role`, { role_key: roleKey });
      toast.success("Rol atribuit");
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, role_key: roleKey } : x));
      if (u.id === user?.id) await refreshPerms();
    } catch (e) { toast.error(apiError(e)); }
  };

  const newUser = () => {
    setUserDraft({ name: "", email: "", password: genPassword(), role_key: roles[0]?.key || "" });
  };

  const saveUser = async () => {
    const d = userDraft;
    if (!d.name || d.name.trim().length < 2) { toast.error("Numele este obligatoriu"); return; }
    if (!d.email || !d.email.includes("@")) { toast.error("Email invalid"); return; }
    if (!d.password || d.password.length < 6) { toast.error("Parola trebuie sa aiba minim 6 caractere"); return; }
    if (!d.role_key) { toast.error("Alege un rol"); return; }
    setSavingUser(true);
    try {
      await api.post("/users", { name: d.name.trim(), email: d.email.trim(), password: d.password, role_key: d.role_key });
      toast.success("Utilizator creat");
      setUserDraft(null);
      await load();
    } catch (e) { toast.error(apiError(e, "Nu s-a putut crea utilizatorul")); }
    finally { setSavingUser(false); }
  };

  const toggleActive = async (u) => {
    const next = !u.active;
    if (!next && !window.confirm(`Dezactivezi utilizatorul "${u.name || u.email}"?`)) return;
    try {
      await api.patch(`/users/${u.id}/active`, { active: next });
      toast.success(next ? "Utilizator activat" : "Utilizator dezactivat");
      setUsers((prev) => prev.map((x) => x.id === u.id ? { ...x, active: next } : x));
    } catch (e) { toast.error(apiError(e)); }
  };

  const ctxLabel = scope === "platform" ? "Platforma Yvola" : "Tenant selectat";

  return (
    <div>
      <PageHeader title="Utilizatori si roluri"
        subtitle="Defineste roluri cu drepturi pe fiecare ecran si atribuie-le utilizatorilor."
        actions={canAdd ? <Btn onClick={newRole} data-testid="util-new-role-btn"><Plus className="w-4 h-4" /> Rol nou</Btn> : null} />
      <AjutorButton file="ajutor_contacte.pdf" />

      {isSuperAdmin && (
        <p className="text-xs text-slate-500 mb-4" data-testid="util-scope-note">
          Context: {ctxLabel}. {tenantId ? "Gestionezi rolurile tenantului selectat din antet." : "Selecteaza un tenant in antet pentru a-i gestiona rolurile."}
        </p>
      )}

      {loading ? <Spinner /> : (
        <div className="space-y-8">
          <div>
            <h2 className="text-base font-semibold text-slate-800 mb-3">Roluri</h2>
            {roles.length === 0 ? (
              <Card><Empty icon={UserCog} title="Niciun rol" hint="Creeaza primul rol." /></Card>
            ) : (
              <Card className="overflow-hidden">
                <table className="w-full text-sm" data-testid="util-roles-table">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-100">
                      <th className="px-5 py-3 font-medium">Rol</th>
                      <th className="px-5 py-3 font-medium">Tip</th>
                      <th className="px-5 py-3 font-medium text-right">Actiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roles.map((r) => (
                      <tr key={r.id} className="border-b border-slate-50" data-testid={`util-role-row-${r.key}`}>
                        <td className="px-5 py-3 font-medium text-slate-900">{r.name}</td>
                        <td className="px-5 py-3">
                          <Badge color={r.system ? "blue" : "green"}>{r.system ? "sistem" : "personalizat"}</Badge>
                        </td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          <button onClick={() => editRole(r)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-700" data-testid={`util-role-edit-${r.key}`}>
                            <Pencil className="w-4 h-4" />
                          </button>
                          {canDelete && !r.system && (
                            <button onClick={() => delRole(r)} className="p-1.5 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-600" data-testid={`util-role-delete-${r.key}`}>
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-slate-800">Utilizatori</h2>
              {canAdd && (
                <Btn onClick={newUser} data-testid="util-new-user-btn">
                  <UserPlus className="w-4 h-4" /> Utilizator nou
                </Btn>
              )}
            </div>
            {users.length === 0 ? (
              <Card><Empty icon={UserCog} title="Niciun utilizator" /></Card>
            ) : (
              <Card className="overflow-hidden">
                <table className="w-full text-sm" data-testid="util-users-table">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-100">
                      <th className="px-5 py-3 font-medium">Nume</th>
                      <th className="px-5 py-3 font-medium">Email</th>
                      <th className="px-5 py-3 font-medium">Rol</th>
                      <th className="px-5 py-3 font-medium">Status</th>
                      <th className="px-5 py-3 font-medium text-right">Actiuni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-slate-50" data-testid={`util-user-row-${u.id}`}>
                        <td className="px-5 py-3 text-slate-900">{u.name || "—"}</td>
                        <td className="px-5 py-3 text-slate-600">{u.email}</td>
                        <td className="px-5 py-3">
                          <select
                            data-testid={`util-user-role-select-${u.id}`}
                            value={u.role_key || ""}
                            disabled={!canEdit}
                            onChange={(e) => assignRole(u, e.target.value)}
                            className="px-3 py-2 rounded-lg bg-white border border-slate-200 text-sm outline-none focus:border-emerald-500 disabled:opacity-60">
                            {!roles.some((r) => r.key === u.role_key) && u.role_key && (
                              <option value={u.role_key}>{u.role_key}</option>
                            )}
                            {roles.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
                          </select>
                        </td>
                        <td className="px-5 py-3">
                          <Badge color={u.active ? "green" : "red"} data-testid={`util-user-status-${u.id}`}>
                            {u.active ? "activ" : "dezactivat"}
                          </Badge>
                        </td>
                        <td className="px-5 py-3 text-right whitespace-nowrap">
                          {canEdit && u.id !== user?.id && (
                            <button onClick={() => toggleActive(u)}
                              className="px-3 py-1.5 rounded-md border border-slate-200 text-xs text-slate-600 hover:bg-slate-50"
                              data-testid={`util-user-toggle-active-${u.id}`}>
                              {u.active ? "Dezactiveaza" : "Activeaza"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </div>
        </div>
      )}

      {draft && (
        <RoleEditor draft={draft} setDraft={setDraft} modules={modules} actions={actions}
          canEdit={canEdit || canAdd} onSave={saveRole} onClose={() => setDraft(null)} saving={saving} />
      )}

      {userDraft && (
        <UserForm draft={userDraft} setDraft={setUserDraft} roles={roles}
          onSave={saveUser} onClose={() => setUserDraft(null)} saving={savingUser} />
      )}
    </div>
  );
}
